// 頁內確認對話框 —— 取代原生 window.confirm()。
//
// 2026-08-06 使用者:「全站 49 個一次換掉」。
//
// 為什麼非換不可（不只是好看）：原生 confirm() 會**同步凍結整個分頁的 JS**。
// 8/3 使用者在簽核詳情頁按「撤回」，那顆原生對話框把分頁卡死到沒辦法用程式關掉，
// 只能另開分頁重做。 在 Chrome 裡它還可能被使用者勾「不再顯示」而永久停用 ——
// 那會讓刪除變成「按了沒反應」，最糟的失敗模式。
//
// API 刻意做成跟原生同形，替換是機械式的：
//     if (confirm('刪除?')) doIt()
//   → if (await confirm({ message: '刪除?' })) doIt()
//
// 回傳 Promise<boolean>；取消 / Esc / 點背景都 resolve(false)。

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { decisionCls } from '../components/form/decision-button.ts';

export interface ConfirmOptions {
  /** 主要問句。 保持一句話、講清楚會發生什麼。 */
  message: string;
  /** 補充說明（不可復原、連帶影響…）。 */
  detail?: string;
  /** 確認鈕文字。 預設「確定」；破壞性動作請寫實際動作（「刪除」）。 */
  confirmLabel?: string;
  cancelLabel?: string;
  /** 破壞性 → 紅色確認鈕。 */
  danger?: boolean;
}

type Resolver = (ok: boolean) => void;

const ConfirmContext = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<Resolver | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setState(o);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((ok: boolean) => {
    setState(null);
    const r = resolverRef.current;
    resolverRef.current = null;
    r?.(ok);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmDialog options={state} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

/**
 * 取得確認函式。 必須包在 <ConfirmProvider> 底下（App.tsx 已掛）。
 *
 * ⚠ 沒有 Provider 時**不會**默默放行 —— 那等於把「請確認」變成「直接做」，
 * 對刪除來說是最危險的失敗方式。 這裡拋錯，讓它在開發階段就爆出來。
 */
export function useConfirm(): (o: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm 必須在 <ConfirmProvider> 內使用');
  return ctx;
}

function ConfirmDialog({
  options,
  onSettle,
}: {
  options: ConfirmOptions;
  onSettle: (ok: boolean) => void;
}) {
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    okRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSettle(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSettle]);

  const { message, detail, confirmLabel, cancelLabel, danger } = options;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* 背景 —— 點擊等同取消 */}
      <button
        type="button"
        aria-label="取消"
        className="absolute inset-0 bg-slate-900/40 cursor-default"
        onClick={() => onSettle(false)}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={message}
        className="relative w-full max-w-lg rounded-lg bg-white shadow-xl border border-slate-200 p-5 max-h-[90vh] overflow-y-auto"
      >
        <div className="text-sm font-medium text-slate-900 leading-relaxed">{message}</div>
        {detail && (
          <p className="mt-2 text-xs text-slate-500 leading-relaxed whitespace-pre-line">
            {detail}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => onSettle(false)} className={decisionCls('quiet')}>
            {cancelLabel ?? '取消'}
          </button>
          {/* 2026-08-27 使用者：「往前的是綠色(實心)，往後是紅色 但是是紅色框就好，
              不要有紅色實心」。 這個確認框是簽核流程的一部分（駁回 / 取消不付款 /
              作廢 按下去先跳它），所以照同一條規則走。
              ⚠ 它是全站共用的 —— 刪成員 / 清資料那些破壞性確認也跟著變成紅框。
              一致性的代價是「最後一道確認」不再靠顏色喊，改靠**文案**：那些呼叫端的
              message / detail 本來就寫著「救不回來」之類的話。 */}
          <button
            ref={okRef}
            type="button"
            onClick={() => onSettle(true)}
            className={decisionCls(danger ? 'back' : 'go')}
          >
            {confirmLabel ?? '確定'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 給非元件情境（極少）—— 目前只有 Drawer 的 dirty 守門在 event handler 裡用。 */
export function useConfirmDiscard() {
  const confirm = useConfirm();
  return useMemo(
    () => () =>
      confirm({
        message: '尚未儲存的變更會遺失，確定關閉？',
        confirmLabel: '關閉',
        danger: true,
      }),
    [confirm],
  );
}
